import PageContainer from 'Components/PageContainer'
import { STORE_KEY_AUTH } from 'Constants'

import {
  evalCondition,
  notifyParent,
  sessionId,
  sessionStoreGet,
  useConsoleTracking,
  usePageTracking,
  useSessionCheck
} from 'mailup-react-helpers'
import { PageHeader } from 'mailup-web-components'
import AuthPage from 'Pages/AuthPage'
import BlankPage from 'Pages/BlankPage'
import Campaign from 'Pages/Campaign'
import ContentsCampaignList from 'Pages/ContentsCampaignList'
import DynamicContentList from 'Pages/DynamicContentList'
import SourceEdit from 'Pages/SourceEdit'
import SourcesList from 'Pages/SourcesList'
import SourceViewContent from 'Pages/SourceViewContent'
import Task from 'Pages/Task'
import TasksList from 'Pages/TasksList'
import Workflow from 'Pages/Workflow'
import WorkflowsList from 'Pages/WorkflowsList'
import WorkflowStatistics from 'Pages/WorkflowStatistics'

import { BrowserRouter, Route, Switch, useRouteMatch } from 'react-router-dom'
import { RecoilRoot } from 'recoil'
import ErrorBoundary from './containers/ErrorBoundary'
import Locale from './containers/Locale'

import NoAuthPage from 'Pages/NoAuthPage'

const NoMatchPage = () => {
  return (
    <>
      <PageHeader breadcrumbsItems={[]} />
      <PageContainer data-mu-test='no-match-page' />
    </>
  )
}

const withAuth = (Component, condition) => {
  const WrappedComponent = (props) => {
    const auth = sessionStoreGet(STORE_KEY_AUTH)
    const routeMatch = useRouteMatch()

    if (!auth) {
      notifyParent('redirectMainFrame', [
        `automation_app_redirector.aspx?r=${routeMatch.url}`
      ])
    }

    if (condition) {
      const conditionIsSatisfied = evalCondition(condition)
      if (!conditionIsSatisfied) {
        return <NoAuthPage />
      }
    }

    return <Component {...props} />
  }

  return WrappedComponent
}
// eslint-disable-next-line
const withPageTracking = (Component) => (props) => {
  // eslint-disable-next-line
  usePageTracking(props.path)
  useConsoleTracking()

  return <Component {...props} />
}

const CustomRoute = withAuth(withPageTracking(Route))

const App = () => {
  useSessionCheck('automation_app_redirector.aspx?r=/automation/sources/list')

  return (
    <RecoilRoot>
      <ErrorBoundary>
        <Locale>
          <BrowserRouter basename={sessionId}>
            <Switch>
              <Route path='/' component={BlankPage} exact />
              <Route path='/auth' component={AuthPage} />

              <CustomRoute
                path='/automation/source/:idType(1|2)/:idSource'
                component={withAuth(
                  SourceEdit,
                  'uo:NL AND uo:ENHTRIGGEREDUSE AND co:ENHTRIGGEREDUSE AND uo:CONTENTSSOURCES'
                )}
              />
              <CustomRoute
                path='/automation/sources/list'
                component={withAuth(SourcesList, 'uo:NL')}
              />
              <CustomRoute
                path='/automation/source/viewContents/:idType(1|2)/:idSource'
                component={withAuth(
                  SourceViewContent,
                  'uo:NL AND uo:ENHTRIGGEREDUSE AND co:ENHTRIGGEREDUSE AND uo:CONTENTSSOURCES'
                )}
              />
              <CustomRoute
                path='/automation/tasks/list'
                component={withAuth(
                  TasksList,
                  '(uo:NL OR uo:SMS) AND (uo:ENHTRIGGEREDUSE AND co:ENHTRIGGEREDUSE)'
                )}
              />
              <CustomRoute
                path='/automation/task/:idTask?'
                component={withAuth(
                  Task,
                  '(uo:NL OR uo:SMS) AND (uo:ENHTRIGGEREDUSE AND co:ENHTRIGGEREDUSE)'
                )}
              />
              <CustomRoute
                path='/automation/workflows/list'
                component={withAuth(
                  WorkflowsList,
                  '(uo:NL OR uo:SMS) AND (uo:ENHTRIGGEREDUSE AND co:ENHTRIGGEREDUSE)'
                )}
              />
              <CustomRoute
                path='/automation/workflow/:idWorkflow/edit'
                component={Workflow}
              />
              <CustomRoute
                path='/automation/workflows/statistics/:idWorkflow'
                component={withAuth(
                  WorkflowStatistics,
                  '(uo:NL OR uo:SMS) AND (uo:ENHTRIGGEREDUSE AND co:ENHTRIGGEREDUSE)'
                )}
              />
              <CustomRoute
                path='/automation/dynamiccontent/list'
                component={withAuth(DynamicContentList, 'uo:DYNFLD')}
              />
              <CustomRoute
                path='/automation/contentsCampaign/list'
                component={withAuth(
                  ContentsCampaignList,
                  'uo:NL AND uo:ENHTRIGGEREDUSE AND co:ENHTRIGGEREDUSE'
                )}
              />
              <CustomRoute
                path='/automation/contentsCampaign/:idCampaign/:step?'
                component={withAuth(
                  Campaign,
                  'uo:NL AND uo:ENHTRIGGEREDUSE AND co:ENHTRIGGEREDUSE'
                )}
              />

              <CustomRoute path='/noauth' component={NoAuthPage} exact />
              <CustomRoute component={NoMatchPage} />
            </Switch>
          </BrowserRouter>
        </Locale>
      </ErrorBoundary>
    </RecoilRoot>
  )
}

export default App
